import React from "react";
import CardBase from "@/components/cards/CardBase";
import { Badge } from "@/components/ui/badge";
import IconProvider from "@/components/ui/IconProvider";
import FavoriteButton from "@/components/ui/FavoriteButton";
import { DraftBadge } from "@/components/ui/DraftBadge";
import { StyledTextDisplay } from "@/components/ui/StyledTextDisplay";
import { buildFolderBreadcrumbs } from "@/lib/stores/folderStore";
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";
import { getTagById, getTagByName } from "@/lib/stores/tagsStore";
import { formatVersion } from "@/lib/utils/versionUtils";
import { Version } from "@/lib/stores/storeTypes";



interface GlossaryWidgetProps {
  id?: string;
  gameId?: string;
  title: string;
  tags: string[];
  description?: string;
  lastUpdate?: string; // ISO date string or formatted date
  version?: Version | number | string; // Support Version object, number, or formatted string
  folder?: string;
  itemType?: "library" | "documents" | "templates" | "dialogs"; // Add itemType prop for breadcrumbs
  icon?: React.ReactNode;
  iconBg?: string; // Tailwind color class or CSS color
  shadowType?: "black" | "gradient" | "none" | "color" | "status" | "template";
  shadowColor?: string;
  statusColor?: string; // Color from status workflow
  hasVersionMismatch: boolean;
  status?: string; // Changed from "draft" | "published" to string to support custom statuses
  owner?: string;
  onDelete?: (id: string, gameId: string) => void;
  onEdit?: (id: string) => void;
}

const GlossaryWidget: React.FC<GlossaryWidgetProps> = ({
  id,
  gameId,
  title,
  tags = [],
  description,
  lastUpdate,
  version,
  folder,
  itemType = "library",
  shadowType = "black",
  shadowColor = "#000000",
  hasVersionMismatch,
  owner,
  status,
  onDelete,
  onEdit,
}) => {
  const { selectedGame } = useSimplifiedUserData();
  const descriptionContainerRef = React.useRef<HTMLDivElement>(null);
  const [maxLines, setMaxLines] = React.useState<number>(15);

  // Calculate max lines based on container height
  React.useEffect(() => {
    if (!descriptionContainerRef.current) return;

    const updateMaxLines = () => {
      const container = descriptionContainerRef.current;
      if (!container) return;

      const containerHeight = container.clientHeight;
      // Assuming line-height of 1.625 (leading-relaxed) and text-sm (14px = 0.875rem)
      const lineHeight = 14 * 1.625; // approximately 22.75px per line
      const calculatedLines = Math.floor(containerHeight / lineHeight);
      
      setMaxLines(Math.max(1, calculatedLines)); // At least 1 line
    };

    updateMaxLines();

    // Recalculate on window resize
    const resizeObserver = new ResizeObserver(updateMaxLines);
    resizeObserver.observe(descriptionContainerRef.current);

    return () => resizeObserver.disconnect();
  }, [description]);
  // Generate breadcrumbs for the item's folder - always show full path from root
  const breadcrumbs = React.useMemo(() => {
    if (!selectedGame?.id || !folder) return [];
    return buildFolderBreadcrumbs(
      selectedGame.id,
      itemType,
      folder,
      null // Always pass null to show full path from root
    );
  }, [selectedGame?.id, folder, itemType]);

  // Helper function to truncate breadcrumbs if they get too long
  const renderBreadcrumbs = () => {
    if (breadcrumbs.length === 0) return null;
    const maxBreadcrumbs = 3; // Maximum number of breadcrumbs to show
    const shouldTruncate = breadcrumbs.length > maxBreadcrumbs;

    if (shouldTruncate) {
      // Show first, "...", and last breadcrumbs
      const firstPart = breadcrumbs.slice(0, 1);
      const lastPart = breadcrumbs.slice(-2);
      const displayBreadcrumbs = [...firstPart, "...", ...lastPart];

      return (
        <div className="flex items-center gap-1 text-xs text-gray-500 truncate">
          <span className="truncate">{displayBreadcrumbs.join(" / ")}</span>
        </div>
      );
    } else {
      return (
        <div className="flex items-center gap-1 text-xs text-gray-500 truncate">
          <span className="truncate">{breadcrumbs.join(" / ")}</span>
        </div>
      );
    }
  };
  //get tags from tags store
  const usedTags = tags
    .map((tag: string) => getTagByName(tag))
    .filter((tag): tag is NonNullable<typeof tag> => tag !== undefined && tag !== null);

  return (
    <CardBase
      className="flex flex-col h-full w-full flex-1"
      shadowType={shadowType}
      shadowColor={shadowColor}
    >
      <div className="flex items-center gap-3 p-4 pb-2 border-b border-gray-300 shrink-0">
        <div className="flex flex-col flex-1 min-w-0">
          <div className="text-lg font-semibold text-foreground leading-tight truncate">
            {title}
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-600">
            <span className="truncate">Created by {owner}</span>
            {version !== undefined && version !== null && (
              <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded font-mono">
                {typeof version === 'object' 
                  ? `v${formatVersion(version)}`
                  : `v${version}`
                }
              </span>
            )}
            <DraftBadge status={status} />
          </div>
        </div>
        {/* Favorite Button */}
        {id && (
          <FavoriteButton
            itemId={id}
            itemType="library"
            size="sm"
            variant="ghost"
          />
        )}
      </div>

      {/* Description */}
      {description && (
        <div 
          ref={descriptionContainerRef}
          className="px-4 pt-2 pb-2 text-foreground flex-1 min-h-0 overflow-hidden flex items-start"
        >
          <StyledTextDisplay 
            content={description} 
            maxLines={maxLines}
            className="text-sm w-full"
          />
        </div>
      )}

      {/* Last update */}
      {lastUpdate && (
        <div className="px-4 pb-2 mt-auto text-xs text-gray-600 shrink-0">
          Last update: {lastUpdate}
        </div>
      )}

      {/* Action buttons - only show if onEdit or onDelete is provided */}
      {(onEdit || onDelete || hasVersionMismatch) && (
        <div className="flex justify-between items-center p-4 pb-2 border-t border-gray-300 shrink-0">
          {/* Left side - Warning icon for version mismatch */}
          <div className="flex items-center gap-2">
            {hasVersionMismatch && itemType === "library" && (
              <div
                className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  // Use window.location.href to navigate within the platform
                  window.location.href = `/library/${id}/migrate`;
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                title="Click to view migration details"
              >
                <IconProvider
                  iconstring="warning"
                  size={14}
                  color="#F59E0B"
                  hoverColor="#D97706"
                />
                <span className="text-xs text-amber-600 font-medium">
                  Version mismatch
                </span>
              </div>
            )}
          </div>

          {/* Right side - Edit/Delete buttons and Breadcrumbs */}
          <div className="flex justify-between items-center min-w-0 flex-1">
            {/* Action buttons on the left */}
            <div className="flex gap-3">
              {onEdit && id && (
                <div
                  className="cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onEdit(id);
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  <IconProvider
                    iconstring="edit"
                    size={14}
                    color="#000000"
                    hoverColor="#E0E0E0"
                  />
                </div>
              )}
              {onDelete && id && gameId && (
                <div
                  className="cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onDelete(id, gameId);
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  <IconProvider
                    iconstring="trash"
                    size={14}
                    color="#000000"
                    hoverColor="#FA8072"
                  />
                </div>
              )}
            </div>

            {/* Breadcrumbs on the right */}
            {renderBreadcrumbs()}
          </div>
        </div>
      )}
    </CardBase>
  );
};

export default GlossaryWidget;
